// src/hooks/useAuthentication.ts
import { ShopifySession } from "~/types/auth";
import { useNavigate } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import { useState, useEffect } from "react";

// Simplified approach without direct App Bridge dependencies
const useAuthentication = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const SESSION_KEY = 'shopifySession';

    // In-memory storage as primary strategy
    const [memorySession, setMemorySession] = useState<ShopifySession | null>(null);

    // Load from sessionStorage on initial mount if available
    useEffect(() => {
        try {
            const savedSession = sessionStorage.getItem(SESSION_KEY);
            if (savedSession) {
                setMemorySession(JSON.parse(savedSession));
            }
        } catch (error) {
            console.warn("Failed to load session from storage:", error);
        }
    }, []);

    const storeShopifySession = (
        shop: string,
        hmac: string,
        public_id: string,
        shopifyAdminUrl: string,
        appName: string
    ) => {
        const newAppName = appName.replace(/\s/g, '_').toLowerCase();

        const sessionData: ShopifySession = {
            shop,
            hmac,
            public_id,
            shopifyAdminUrl,
            appName: newAppName
        };

        // Store in memory state (primary storage)
        setMemorySession(sessionData);

        // Try sessionStorage as persistence layer
        try {
            sessionStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
        } catch (error) {
            console.warn("Failed to store in sessionStorage:", error);
        }

        // If we're in an iframe, try to communicate with parent frame
        if (window.top !== window.self) {
            try {
                // Post message to parent frame to store session
                window.parent.postMessage({
                    type: 'STORE_SHOPIFY_SESSION',
                    payload: sessionData
                }, '*'); // In production, specify target origin for security
            } catch (error) {
                console.warn("Failed to communicate with parent frame:", error);
            }
        }

        return true;
    };

    const getShopifySession = (): ShopifySession | null => {
        // Primary source is memory
        if (memorySession) {
            return memorySession;
        }

        // Fallback to sessionStorage
        try {
            const sessionData = sessionStorage.getItem(SESSION_KEY);
            if (sessionData) {
                const parsed = JSON.parse(sessionData);
                // Update memory storage for future calls
                setMemorySession(parsed);
                return parsed;
            }
        } catch (error) {
            console.warn("Failed to get session from sessionStorage:", error);
        }

        return null;
    };

    const validateShopifyDomain = (shop: string): string => {
        // Remove protocol and www if present
        let domain = shop.replace(/^https?:\/\//, '').replace(/^www\./, '');

        // Add myshopify.com if not present
        if (!domain.includes('myshopify.com')) {
            domain = `${domain}.myshopify.com`;
        }

        // Validate the domain format
        const shopifyDomainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com$/;
        if (!shopifyDomainRegex.test(domain)) {
            throw new Error('Invalid Shopify domain');
        }

        return domain;
    };

    const clearShopifySession = () => {
        // Clear from memory
        setMemorySession(null);

        // Clear from sessionStorage
        try {
            sessionStorage.removeItem(SESSION_KEY);
        } catch (error) {
            console.warn("Failed to clear session from sessionStorage:", error);
        }

        // If in iframe, notify parent
        if (window.top !== window.self) {
            try {
                window.parent.postMessage({
                    type: 'CLEAR_SHOPIFY_SESSION'
                }, '*'); // In production, specify target origin for security
            } catch (error) {
                console.warn("Failed to communicate with parent frame:", error);
            }
        }
    };

    const redirectToApplication = (public_id: string, params: any) => {
        // Check if we're in an iframe (Shopify Admin)
        let isInIframe = window.top !== window.self;

        // if localhost always set isInIframe to true
        if (window.location.hostname === 'localhost') {
            isInIframe = true;
        }

        const session = getShopifySession();
        const { appName, shopifyAdminUrl } = session || {};

        if (!shopifyAdminUrl || !appName) {
            toast({
                title: 'Error',
                description: 'Invalid session, please log in again',
                status: 'error',
                duration: 5000,
                isClosable: true
            });
            clearShopifySession();
            navigate(`/app/${public_id}/login`);
            return;
        }

        if (isInIframe) {
            const queryString = new URLSearchParams(params).toString();

            // We're already in Shopify Admin, just load the app
            navigate(`/c-panel/${appName}/dashboard?${queryString}`);
        } else {
            // Redirect to Shopify Admin
            window.location.href = shopifyAdminUrl;
        }
    };

    const getBaseAppUrl = () => {
        const session = getShopifySession();
        return session ? `c-panel/${session.appName?.toLowerCase()}/` : '';
    };

    // Add a listener for cross-frame communication (optional enhancement)
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            // Only process messages with the right format
            if (event.data && event.data.type === 'SHOPIFY_SESSION_UPDATE') {
                setMemorySession(event.data.payload);
                try {
                    sessionStorage.setItem(SESSION_KEY, JSON.stringify(event.data.payload));
                } catch (error) {
                    console.warn("Failed to store session from message:", error);
                }
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    return {
        storeShopifySession,
        validateShopifyDomain,
        clearShopifySession,
        redirectToApplication,
        getShopifySession,
        getBaseAppUrl
    };
};

export default useAuthentication;